import { useState } from 'react'
import { useTranslation } from 'react-i18next'

function TovExamplesSection({ examples, onChange }) {
  const { t } = useTranslation('settings')
  const [showConfirmDelete, setShowConfirmDelete] = useState(null)

  // Add new example
  const handleAddExample = () => {
    const newId = examples.length > 0 ? Math.max(...examples.map(e => e.id)) + 1 : 1
    const newExamples = [
      ...examples,
      {
        id: newId,
        text: '',
        order: examples.length
      }
    ]
    onChange(newExamples)
  }

  // Update example text
  const handleExampleChange = (id, text) => {
    const newExamples = examples.map(example =>
      example.id === id ? { ...example, text } : example
    )
    onChange(newExamples)
  }

  // Delete example
  const handleDeleteExample = (id) => {
    const newExamples = examples
      .filter(example => example.id !== id)
      .map((example, index) => ({ ...example, order: index }))
    onChange(newExamples)
    setShowConfirmDelete(null)
  }

  // Get status indicator
  const getStatusIndicator = () => {
    const count = examples.length
    if (count === 0) {
      return {
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        borderColor: 'border-red-200',
        label: t('tov.status.none.label'),
        message: t('tov.status.none.message')
      }
    } else if (count >= 1 && count <= 2) {
      return {
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-100',
        borderColor: 'border-yellow-200',
        label: t('tov.status.few.label', { count }),
        message: t('tov.status.few.message')
      }
    } else if (count >= 3 && count <= 10) {
      return {
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        borderColor: 'border-green-200',
        label: t('tov.status.optimal.label', { count }),
        message: t('tov.status.optimal.message')
      }
    } else {
      return {
        color: 'text-red-600',
        bgColor: 'bg-red-100',
        borderColor: 'border-red-200',
        label: t('tov.status.max.label', { count }),
        message: t('tov.status.max.message')
      }
    }
  }

  const status = getStatusIndicator()

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{t('tov.title')}</h2>
          <p className="mt-1 text-sm text-gray-600">
            {t('tov.subtitle')}
          </p>
        </div>
        <div className={`px-3 py-1 text-sm font-medium rounded-full border ${status.bgColor} ${status.color} ${status.borderColor}`}>
          {status.label}
        </div>
      </div>

      {/* Status Message */}
      <div className={`px-4 py-3 rounded-lg border ${status.bgColor} ${status.borderColor}`}>
        <p className={`text-sm ${status.color}`}>
          {status.message}
        </p>
      </div>

      {/* Examples List */}
      {examples.length > 0 ? (
        <div className="space-y-4">
          {examples.map((example, index) => {
            const charCount = example.text.length
            const isValid = charCount >= 20 && charCount <= 280
            const isEmpty = charCount === 0
            const isTooShort = charCount > 0 && charCount < 20
            const isTooLong = charCount > 280

            return (
              <div
                key={example.id}
                className={`p-4 border-2 rounded-lg transition-all ${
                  isEmpty
                    ? 'border-gray-200 bg-gray-50'
                    : isValid
                    ? 'border-green-200 bg-green-50'
                    : 'border-red-200 bg-red-50'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    {t('tov.example.label', { number: index + 1 })}
                  </label>
                  <button
                    onClick={() => setShowConfirmDelete(example.id)}
                    className="text-red-600 hover:text-red-700 text-sm font-medium transition-colors"
                  >
                    {t('tov.example.button.delete')}
                  </button>
                </div>

                <textarea
                  value={example.text}
                  onChange={(e) => handleExampleChange(example.id, e.target.value)}
                  placeholder={t('tov.example.placeholder')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                  rows={3}
                  style={{ minHeight: '80px' }}
                  onInput={(e) => {
                    e.target.style.height = 'auto'
                    e.target.style.height = e.target.scrollHeight + 'px'
                  }}
                />

                <div className="flex items-center justify-between mt-2">
                  <div className="text-xs text-gray-500">
                    {isTooShort && (
                      <span className="text-red-600">{t('tov.example.validation.minLength')}</span>
                    )}
                    {isTooLong && (
                      <span className="text-red-600">{t('tov.example.validation.maxLength')}</span>
                    )}
                    {isValid && (
                      <span className="text-green-600">{t('tov.example.validation.valid')}</span>
                    )}
                    {isEmpty && (
                      <span className="text-gray-500">{t('tov.example.button.add')}</span>
                    )}
                  </div>
                  <div className={`text-sm font-medium ${
                    charCount > 280 ? 'text-red-600' :
                    charCount >= 20 ? 'text-green-600' :
                    charCount > 0 ? 'text-yellow-600' :
                    'text-gray-500'
                  }`}>
                    {charCount}/280
                  </div>
                </div>

                {/* Confirmation Dialog */}
                {showConfirmDelete === example.id && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setShowConfirmDelete(null)}></div>
                    <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('tov.delete.title')}</h3>
                      <p className="text-gray-600 mb-6">
                        {t('tov.delete.message', { number: index + 1 })}
                      </p>
                      <div className="flex justify-end space-x-3">
                        <button
                          onClick={() => setShowConfirmDelete(null)}
                          className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          {t('tov.delete.button.cancel')}
                        </button>
                        <button
                          onClick={() => handleDeleteExample(example.id)}
                          className="px-4 py-2 text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                        >
                          {t('tov.delete.button.confirm')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-600 mb-4">
            {t('tov.empty')}
          </p>
        </div>
      )}

      {/* Add Example Button */}
      {examples.length < 10 && (
        <button
          onClick={handleAddExample}
          className="w-full py-3 text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg font-medium transition-colors border-2 border-primary-200 hover:border-primary-300"
        >
          + {t('tov.example.button.add')}
        </button>
      )}

      {examples.length >= 10 && (
        <div className="text-center py-3 text-sm text-gray-500">
          {t('tov.maxReached')}
        </div>
      )}
    </div>
  )
}

export default TovExamplesSection
